import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts"
import { formatCurrency, formatNumber, formatPercent, IMPUESTOS_LABELS, colorPara } from "../helpers"
import CopiarNit from "../CopiarNit"

const TooltipPie = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null
  const d = payload[0].payload
  return (
    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
      <div style={{ fontWeight: 700 }}>{d.label}</div>
      <div>{formatCurrency(d.valor)}</div>
      <div style={{ color: "#94a3b8", fontSize: 11 }}>{formatPercent(d.pct)}</div>
    </div>
  )
}

TooltipPie.propTypes = { active: PropTypes.bool, payload: PropTypes.array }

const ImpuestosTab = ({ data }) => {
  const terceros = data?.terceros || []
  const [impuestoSel, setImpuestoSel] = useState("")

  // Agregados globales por tipo de impuesto
  const totales = useMemo(() => {
    const acc = {}
    IMPUESTOS_LABELS.forEach(({ key }) => { acc[key] = 0 })
    terceros.forEach(t => {
      IMPUESTOS_LABELS.forEach(({ key }) => {
        acc[key] += Number(t.detalle_impuestos?.[key]) || 0
      })
    })
    return IMPUESTOS_LABELS
      .map(({ key, label }) => ({ key, label, valor: acc[key] }))
      .filter(i => i.valor > 0)
      .sort((a, b) => b.valor - a.valor)
  }, [terceros])

  const totalGeneral = totales.reduce((s, i) => s + i.valor, 0)
  const datosPie = totales.map(i => ({
    ...i,
    pct: totalGeneral > 0 ? (i.valor / totalGeneral) * 100 : 0,
  }))

  // Si hay un impuesto seleccionado, ranking de terceros que más aportan a ese impuesto
  const rankingPorImpuesto = useMemo(() => {
    if (!impuestoSel) return []
    return [...terceros]
      .map(t => ({
        nit: t.nitreceptor,
        nombre: t.nombre_canonico || t.nombrereceptor || "—",
        valor: Number(t.detalle_impuestos?.[impuestoSel]) || 0,
      }))
      .filter(t => t.valor > 0)
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 20)
  }, [terceros, impuestoSel])

  if (totales.length === 0) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
        <i className="mdi mdi-bank-off-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin impuestos registrados en el período.</div>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Total general + Pie */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
              <i className="mdi mdi-bank me-2 text-warning" />
              Distribución de impuestos
            </h6>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              Total: <strong style={{ color: "#0f172a", fontSize: 16 }}>{formatCurrency(totalGeneral)}</strong>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 380px", minWidth: 320, height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={datosPie} dataKey="valor" nameKey="label" cx="50%" cy="50%" outerRadius={110} innerRadius={50} paddingAngle={1}>
                  {datosPie.map((_, i) => <Cell key={i} fill={colorPara(i)} stroke="#fff" strokeWidth={2} />)}
                </Pie>
                <Tooltip content={<TooltipPie />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ flex: "1 1 360px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Impuesto</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Valor</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>%</th>
                  <th style={{ padding: "10px 12px", width: 100 }} />
                </tr>
              </thead>
              <tbody>
                {datosPie.map((imp, i) => (
                  <tr key={imp.key} style={{ borderBottom: "1px solid #f1f5f9", background: impuestoSel === imp.key ? "#eff6ff" : undefined }}>
                    <td style={{ padding: "10px 12px", color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: colorPara(i) }} />
                      {imp.label}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#0f172a", fontWeight: 600 }}>{formatCurrency(imp.valor)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>{formatPercent(imp.pct)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>
                      <button
                        type="button"
                        className={`btn btn-sm ${impuestoSel === imp.key ? "btn-primary" : "btn-outline-primary"}`}
                        style={{ fontSize: 11, padding: "2px 10px" }}
                        onClick={() => setImpuestoSel(impuestoSel === imp.key ? "" : imp.key)}
                      >
                        {impuestoSel === imp.key ? "Ocultar" : "Ver detalle"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Top terceros del impuesto seleccionado */}
      {impuestoSel && rankingPorImpuesto.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
            <i className="mdi mdi-trophy me-2 text-warning" />
            Top 20 terceros — {totales.find(t => t.key === impuestoSel)?.label}
          </h6>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, width: 40 }}>#</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>NIT</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Nombre</th>
                  <th style={{ padding: "8px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Valor</th>
                </tr>
              </thead>
              <tbody>
                {rankingPorImpuesto.map((t, i) => (
                  <tr key={t.nit} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 12px", color: "#94a3b8", fontSize: 12 }}>{i + 1}</td>
                    <td style={{ padding: "8px 12px" }}><CopiarNit nit={t.nit} /></td>
                    <td style={{ padding: "8px 12px", color: "#0f172a", fontWeight: 500 }}>{t.nombre}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#f59e0b", fontWeight: 600 }}>{formatCurrency(t.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

ImpuestosTab.propTypes = { data: PropTypes.object }

export default ImpuestosTab
